import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from './shared/shared.module';
import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  HttpClientModule,
} from '@angular/common/http';
import { AsksuiteCitrusModule } from 'asksuite-citrus';
import { ErrorHandlerService } from './core/services/error-handler.service';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { LayoutsModule } from './layouts/layouts.module';
import { WithCredentialsInterceptor } from './core/interceptors/with-credentials.interceptor';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { SessionGateInterceptor } from './core/interceptors/session-gate.interceptor';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { ConnectionInterceptor } from './core/interceptors/connection.interceptor';
import { CurrencyPipe, DatePipe, registerLocaleData } from '@angular/common';
import { ReconnectModule } from './modules/reconnect/reconnect.module';
import ptBr from '@angular/common/locales/pt';
import esLocale from '@angular/common/locales/es';
import enLocale from '@angular/common/locales/en';
import { TagsModule } from '@livechat/tags';
import { environment } from '../environments/environment';
import { LogInterceptor } from './core/interceptors/log.interceptor';
import { AnswerTemplateModule } from './libs/answer-templates';
import { COMPANY_SERVICE, TRANSLATOR, USER_SERVICE } from '@livechat/shared';
import { LocalizationService } from './core/services/localization.service';
import { CompanyService } from './core/services/company.service';
import { UserService } from './core/services/user.service';
import { UnauthorizedInterceptor } from './core/interceptors/unauthorized.interceptor';
import { ForbiddenInterceptor } from '@core/interceptors/forbidden.interceptor';
import { TimeoutInterceptor } from './core/interceptors/timeout.interceptor';
import { LIVECHAT_VERSION } from './shared/utils/livechat_version';
import { VERSION } from 'src/environments/version';
import { initializeApp, FirebaseOptions } from 'firebase/app';
import { NAVIGATOR_TOKEN } from './shared/utils/navigator-token';
import { TokenRefreshModalComponent } from './core/components/token-refresh-modal/token-refresh-modal.component';
import { VersionUpdateBannerComponent } from './core/components/version-update-banner/version-update-banner.component';
import { ConnectionExpiredModalComponent } from './core/components/connection-expired-modal/connection-expired-modal.component';
import { NgxDeviceDetectorAdapter } from './shared/adapters/device-detector/ngx-device-detector.adapter';
import { DEVICE_DETECTOR } from './shared/interfaces/device-detector';
import {
  APP_NAME_TOKEN,
  IS_E2E_TOKEN,
  WINDOW_INJECTION_TOKEN,
} from '@core/constants/injection-tokens';

registerLocaleData(ptBr);
registerLocaleData(esLocale);
registerLocaleData(enLocale);

export function createTranslateLoader(http: HttpBackend) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: './assets/citrus-i18n/', suffix: '.json' },
    { prefix: './assets/i18n/', suffix: '.json' },
  ]);
}

initializeApp(environment.fcmPushNotifications as unknown as FirebaseOptions);
@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      defaultLanguage: 'pt',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpBackend],
      },
    }),
    AppRoutingModule,
    ReconnectModule,
    LayoutsModule,
    HttpClientModule,
    SharedModule,
    AsksuiteCitrusModule.forRoot({
      flagUrl: environment.countryFlagsUrl,
    }),
    TagsModule.forRoot({
      baseBffUrl: environment.externalUrls.bff,
    }),
    TokenRefreshModalComponent,
    VersionUpdateBannerComponent,
    ConnectionExpiredModalComponent,
    AnswerTemplateModule.forRoot({
      baseBffUrl: environment.externalUrls.bff,
      translator: {
        provide: TRANSLATOR,
        useClass: LocalizationService,
      },
      companyService: {
        provide: COMPANY_SERVICE,
        useClass: CompanyService,
      },
      userService: {
        provide: USER_SERVICE,
        useClass: UserService,
      },
    }),
  ],
  providers: [
    { provide: Location, useValue: window.location },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: WithCredentialsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SessionGateInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ConnectionInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LogInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnauthorizedInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ForbiddenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TimeoutInterceptor,
      multi: true,
    },
    {
      provide: Storage,
      useValue: window.localStorage,
    },
    {
      provide: WINDOW_INJECTION_TOKEN,
      useValue: window,
    },
    DatePipe,
    CurrencyPipe,
    {
      provide: LIVECHAT_VERSION,
      useValue: VERSION,
    },
    {
      provide: NAVIGATOR_TOKEN,
      useValue: window.navigator,
    },
    {
      provide: DEVICE_DETECTOR,
      useFactory: () => new NgxDeviceDetectorAdapter(),
    },
    {
      provide: IS_E2E_TOKEN,
      useValue: Boolean((window as any).Cypress),
    },
    {
      provide: APP_NAME_TOKEN,
      useValue: 'asksuite-livechat',
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
